import React, { useState, useEffect, useRef } from "react";
import { trackPriceResearchRemark } from "../../../services/savedRemarks";
import { toast } from "../../../utils/notifications";
import { extractPriceFromText, removePriceFromText } from "../../../utils/price";
import { SavedRemarkFile } from "../../../types/savedRemarks";
import type { RFQItem } from "../../../types/rfq";
import { useRemarkSuggestions, RemarkSuggestion } from "../../../hooks/useRemarkSuggestions";
import { applyPrice } from "./itemPricing";
import type { RFQItemUpdate } from "../../../services/rfqItems";

interface UseItemRemarksOptions {
  item: RFQItem;
  /** Writes the row (the row's update mutation). */
  save: (data: RFQItemUpdate) => void;
  setIsUpdating: (updating: boolean) => void;
  /** Remarks can carry a price (pasted text, saved remarks, suggestions). */
  setPrice: (price: string) => void;
  setMarkup: (markup: string) => void;
  setIsPriceModified: (modified: boolean) => void;
}

const remarkTagsOf = (nonRefundable: boolean, limitedStock: boolean): string[] => {
  const tags: string[] = [];
  if (nonRefundable) tags.push('Non-returnable');
  if (limitedStock) tags.push('Limited Stock');
  return tags;
};

/**
 * The item row's remarks: the two tag toggles, the custom remark, the remark
 * popover with its suggestions, and the saved-remarks dialog.
 */
export function useItemRemarks({
  item,
  save,
  setIsUpdating,
  setPrice,
  setMarkup,
  setIsPriceModified,
}: UseItemRemarksOptions) {
  const [isNonRefundable, setIsNonRefundable] = useState(
    Array.isArray(item.remark_tags) && item.remark_tags.includes('Non-returnable')
  );
  const [isLimitedStock, setIsLimitedStock] = useState(
    Array.isArray(item.remark_tags) && item.remark_tags.includes('Limited Stock')
  );
  const [customRemark, setCustomRemark] = useState(item.remarks || '');
  const [isRemarksModified, setIsRemarksModified] = useState(false);

  // Popover state
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const openRemarkPopover = Boolean(anchorEl);

  // Saved remarks dialog state
  const [savedRemarksOpen, setSavedRemarksOpen] = useState(false);

  // Remark suggestions hook - memoize item to prevent unnecessary re-fetches
  const suggestionItem = React.useMemo(() => ({
    item_name: item.item_name,
    model_number: item.model_number,
    brand: item.brand,
    item_type: item.item_type
  }), [item.item_name, item.model_number, item.brand, item.item_type]);

  const {
    suggestions: fetchedSuggestions,
    loading: suggestionsLoading,
    error: suggestionsError,
    applySuggestion
  } = useRemarkSuggestions(
    suggestionItem,
    {
      autoFetch: openRemarkPopover, // Only fetch when popover opens
      minConfidence: 20
    }
  );

  // Price-research suggested remarks metadata
  const priceResearchRemark = React.useMemo(() => {
    const gdb = item.price_sources?.generic_items_database;
    if (!gdb?.suggested_remarks) return null;
    return {
      content: gdb.suggested_remarks,
      price: gdb.price ?? null,
      quoteCount: gdb.quote_count || 0,
      source: gdb.suggested_remarks_source ?? null,
      sourceId: gdb.suggested_remarks_source_id ?? null,
    };
  }, [item.price_sources]);

  // Combine price-research suggested remarks with fetched suggestions
  const suggestions = React.useMemo(() => {
    const safeSuggestions = fetchedSuggestions ?? [];
    if (!priceResearchRemark || customRemark) return safeSuggestions;

    const alreadyIncluded = safeSuggestions.some(
      s => s.content === priceResearchRemark.content
    );
    if (alreadyIncluded) return safeSuggestions;

    const priceSuggestion: RemarkSuggestion = {
      id: -1,
      title: 'From Price Research',
      content: priceResearchRemark.content,
      price: priceResearchRemark.price,
      usageCount: priceResearchRemark.quoteCount,
      score: 100,
      confidence: 90,
      matchReasons: ['Auto-detected during price research'],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    return [priceSuggestion, ...safeSuggestions];
  }, [fetchedSuggestions, priceResearchRemark, customRemark]);

  // Track "suggested" event when the price-research remark is shown in the popover
  const hasSentSuggestedRef = useRef(false);
  useEffect(() => {
    if (
      openRemarkPopover &&
      priceResearchRemark?.source &&
      priceResearchRemark?.sourceId &&
      !customRemark &&
      !hasSentSuggestedRef.current
    ) {
      hasSentSuggestedRef.current = true;
      trackPriceResearchRemark(priceResearchRemark.source, priceResearchRemark.sourceId, 'suggested').catch(() => {});
    }
    if (!openRemarkPopover) {
      hasSentSuggestedRef.current = false;
    }
  }, [openRemarkPopover, priceResearchRemark, customRemark]);

  // Resync from the item unless the user is mid-edit. The triggers are the
  // row's price/indent resync triggers too (it was one effect): the item's
  // fields changing, never the edit flag itself.
  useEffect(() => {
    if (!isRemarksModified) {
      setIsNonRefundable(Array.isArray(item.remark_tags) && item.remark_tags.includes('Non-returnable'));
      setIsLimitedStock(Array.isArray(item.remark_tags) && item.remark_tags.includes('Limited Stock'));
      setCustomRemark(item.remarks || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- see above
  }, [item.remark_tags, item.remarks, item.quoted_price, item.markup_percentage, item.indent_days]);

  // Handle opening the remarks popover
  const handleOpenRemarkPopover = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  // Handle closing the remarks popover
  const handleCloseRemarkPopover = () => {
    setAnchorEl(null);
  };

  // Handle remark toggle changes
  const handleRemarkToggle = (type: 'nonRefundable' | 'limitedStock') => {
    setIsRemarksModified(true);

    // Update local state
    const newNonRefundable = type === 'nonRefundable' ? !isNonRefundable : isNonRefundable;
    const newLimitedStock = type === 'limitedStock' ? !isLimitedStock : isLimitedStock;

    if (type === 'nonRefundable') {
      setIsNonRefundable(newNonRefundable);
    } else {
      setIsLimitedStock(newLimitedStock);
    }

    // Save changes immediately
    const updateData = {
      remark_tags: remarkTagsOf(newNonRefundable, newLimitedStock),
      remarks: customRemark || null  // Preserve existing custom remark
    };

    setIsUpdating(true);
    save(updateData);
  };

  // Save remarks changes
  const handleSaveRemarks = () => {
    if (!isRemarksModified) return;

    const updateData = {
      remark_tags: remarkTagsOf(isNonRefundable, isLimitedStock),
      remarks: customRemark || null  // Ensure null instead of empty string
    };

    setIsUpdating(true);
    save(updateData);
    handleCloseRemarkPopover();
  };

  // Handle paste events to extract price
  const handleRemarkPaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    const pastedText = e.clipboardData.getData('text');

    if (pastedText) {
      // Extract price from pasted text
      const extractedPrice = extractPriceFromText(pastedText);

      if (extractedPrice && !item.price_validated) { // Only set price if not yet validated
        // Set the extracted price, and the markup if we have a cost price
        const applied = applyPrice(item.cost_price, extractedPrice);
        setPrice(applied.price);
        if (applied.markup != null) setMarkup(applied.markup);

        // Remove price from the text that will be pasted
        const cleanedText = removePriceFromText(pastedText, extractedPrice);

        // Prevent default paste and set cleaned text
        e.preventDefault();
        setCustomRemark(customRemark + cleanedText);
        setIsRemarksModified(true);

        // Auto-save both the extracted price and cleaned remarks
        const updateData = {
          quoted_price: extractedPrice,
          markup_percentage: applied.markupPercentage,
          remarks: (customRemark + cleanedText) || null // Save the cleaned remarks too
        };

        setIsUpdating(true);
        save(updateData);

        // Show notification about extracted price
        toast({
          title: "Price extracted & saved",
          description: `Price $${extractedPrice.toFixed(2)} extracted, auto-filled and saved! You can now validate it.`,
          severity: "success",
        });
      } else if (extractedPrice && item.price_validated) {
        // Show notification when price is found but can't be set due to validation
        toast({
          title: "Price already validated",
          description: `Found price $${extractedPrice.toFixed(2)} in text, but current price is already validated. Unvalidate first to auto-extract.`,
          severity: "info",
        });
      }
    }
  };

  // Handle custom remark changes
  const handleCustomRemarkChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsRemarksModified(true);
    setCustomRemark(e.target.value);
  };

  const handleRemarkBlur = () => {
    if (isRemarksModified) {
      handleSaveRemarks();
    }
  };

  // Handle saved remarks functionality
  const handleOpenSavedRemarks = () => {
    setSavedRemarksOpen(true);
  };

  const handleCloseSavedRemarks = () => {
    setSavedRemarksOpen(false);
  };

  const handleInsertSavedRemark = async (remarkContent: string, remarkPrice?: number, _files?: SavedRemarkFile[], remarkId?: number) => {
    // Insert the saved remark content
    setCustomRemark(remarkContent);

    // Insert the price if provided
    const applied = remarkPrice != null ? applyPrice(item.cost_price, remarkPrice) : null;
    if (applied) {
      setPrice(applied.price);
    }

    setSavedRemarksOpen(false);

    // Ensure remarkId is a valid number before saving
    const savedRemarkIdValue = (typeof remarkId === 'number' && remarkId > 0) ? remarkId : null;

    const updateData: RFQItemUpdate = {
      remark_tags: remarkTagsOf(isNonRefundable, isLimitedStock),
      remarks: remarkContent || null,
      saved_remark_id: savedRemarkIdValue  // Store the saved remark ID so we can retrieve files later
    };

    // Include price if provided, and the markup if we have a cost price
    if (remarkPrice != null && applied) {
      updateData.quoted_price = remarkPrice;
      if (applied.markup != null) {
        setMarkup(applied.markup);
        updateData.markup_percentage = applied.markupPercentage;
      }
    }

    // Run mutation immediately (no timeout) so optimistic updates apply right away
    // This ensures saved_remark_id is in the cache when email dialog opens
    setIsUpdating(true);
    save(updateData);
  };

  const handleSelectSuggestion = (suggestion: RemarkSuggestion) => {
    // Track "used" if this is the price-research suggestion
    if (suggestion.id === -1 && priceResearchRemark?.source && priceResearchRemark?.sourceId) {
      trackPriceResearchRemark(priceResearchRemark.source, priceResearchRemark.sourceId, 'used').catch(() => {});
    }

    applySuggestion(
      suggestion,
      item.rfq_item_id,
      (content, suggestedPrice) => {
        setCustomRemark(content);
        setIsRemarksModified(true);

        // Auto-fill price if suggested and not already validated
        if (suggestedPrice && !item.price_validated) {
          setPrice(suggestedPrice.toFixed(2));
          setIsPriceModified(true);
        }

        // Close popover after selection
        handleCloseRemarkPopover();

        // Auto-save remarks and price immediately
        setTimeout(() => {
          // Record which saved remark this came from, so the email
          // preview can find its attached files. The price-research
          // pseudo-suggestion uses id -1 and is not a saved remark.
          const suggestionRemarkId =
            (typeof suggestion.id === 'number' && suggestion.id > 0)
              ? suggestion.id
              : null;

          const updateData: RFQItemUpdate = {
            remark_tags: remarkTagsOf(isNonRefundable, isLimitedStock),
            remarks: content || null,
            saved_remark_id: suggestionRemarkId
          };

          // Include price if suggested, and the markup if we have a cost price
          if (suggestedPrice && !item.price_validated) {
            updateData.quoted_price = suggestedPrice;

            const applied = applyPrice(item.cost_price, suggestedPrice);
            if (applied.markup != null) {
              setMarkup(applied.markup);
              updateData.markup_percentage = applied.markupPercentage;
            }
          }

          setIsUpdating(true);
          save(updateData);
        }, 100);
      }
    );
  };

  // Get remarks display string
  const getRemarksDisplay = () => {
    const remarks: string[] = [];
    if (isNonRefundable) remarks.push("Non-returnable");
    if (isLimitedStock) remarks.push("Limited Stock");
    if (customRemark) remarks.push(customRemark);

    return remarks.length > 0 ? remarks.join(", ") : "-";
  };

  return {
    isNonRefundable,
    isLimitedStock,
    customRemark,
    setIsRemarksModified,
    remarkPopover: {
      open: openRemarkPopover,
      anchorEl,
      onClose: handleCloseRemarkPopover,
      suggestions,
      suggestionsLoading,
      suggestionsError,
      onSelectSuggestion: handleSelectSuggestion,
      customRemark,
      onCustomRemarkChange: handleCustomRemarkChange,
      onPaste: handleRemarkPaste,
      onBlur: handleRemarkBlur,
      onBrowseAll: handleOpenSavedRemarks,
    },
    savedRemarksOpen,
    handleCloseSavedRemarks,
    handleInsertSavedRemark,
    handleOpenRemarkPopover,
    handleRemarkToggle,
    getRemarksDisplay,
  };
}
